import { useToast } from "@expert/common-ui";
import { getLogger } from "@expert/logging";
import { Box, Button, Flex, Text, TextInput } from "@mantine/core";
import { isEmail, useForm } from "@mantine/form";
import { HTTPError } from "ky";
import { type JSX, useState } from "react";
import { useAnalytics } from "@/analytics/hooks/useAnalytics";
import { useVerifiedCustomerStore } from "@/customer-info/store";
import { type EligibleProduct, useProductOfferStore } from "@/sales-tools";
import { getSendLinkCopy } from "@/sales-tools/home-product/components/SendLink/utils/getSendLinkCopy";
import { isChatTask, useMostRecentTask, useSessionId } from "@/sdk";
import { formatPhoneNumber } from "@/shared-utils";
import { useIntegratedFlowStore } from "../../stores/useIntegratedFlowStore";
import { GreenQuote } from "../Icons";
import { SendDisclosuresIcon } from "../Icons/SendDisclosureIcon";
import type { CommunicationType } from "../types";
import { CommunicationTypeSelector } from "./CommunicationTypeSelector";
import { useHandleSendLink } from "./HandleSendLink";
import classes from "./SendLink.module.css";
import { SendLinkFooter } from "./SendLinkFooter";

const logger = getLogger({ module: "SendLink" });

export function SendLink({
    product,
    mdn,
    email,
    updateMDN,
    updateEmail,
    sendLinkCommTypeOverride,
    helpButtonEnabled = false,
}: {
    product: EligibleProduct;
    mdn: string;
    email: string;
    updateMDN: React.Dispatch<React.SetStateAction<string>>;
    updateEmail: React.Dispatch<React.SetStateAction<string>>;
    sendLinkCommTypeOverride?: CommunicationType;
    helpButtonEnabled?: boolean;
}): JSX.Element | null {
    const { dispatcher } = useAnalytics();
    const toast = useToast();
    const sessionId = useSessionId();
    const integratedStep = useIntegratedFlowStore((state) => state.getCurrentStep(sessionId));
    const updateIntegratedStep = useIntegratedFlowStore((state) => state.updateIntegratedStep);
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const [commType, setCommType] = useState<CommunicationType>(sendLinkCommTypeOverride ?? "SMS");
    const [loading, setLoading] = useState(false);
    const isEmailType = () => commType === "EMAIL";
    const mostRecentTask = useMostRecentTask();
    const isChat = isChatTask(mostRecentTask);
    const primaryProducts = useProductOfferStore((state) => state.getPrimaryProducts(sessionId));
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    const sendLink = useHandleSendLink();

    const sanitizePhoneNumber = (phone: string | undefined): string => {
        if (!phone) return "";
        return phone.replace(/\D/g, "");
    };

    const formatInputMDN = (inputMdn: string | undefined): string => {
        if (!inputMdn) return "";
        const sanitized = sanitizePhoneNumber(inputMdn);
        return `+1${sanitized}`;
    };

    const formatInputMDNDisplay = (inputMdn: string): string => {
        const sanitized = sanitizePhoneNumber(inputMdn);
        if (sanitized.length <= 3) return `(${sanitized}`;
        if (sanitized.length <= 6) return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3)}`;
        return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3, 6)}-${sanitized.slice(6, 10)}`;
    };

    const form = useForm({
        initialValues: {
            contact: isEmailType() ? email : formatPhoneNumber(mdn).number,
        },

        validate: {
            contact: (value) => {
                if (!value) {
                    return "This field is required";
                }
                if (isEmailType()) {
                    const validateEmail = isEmail("Invalid email address");
                    return validateEmail(value);
                } else {
                    try {
                        formatPhoneNumber(formatInputMDN(value));
                    } catch {
                        return "Invalid phone number";
                    }
                    return /^\d{10}$/.test(sanitizePhoneNumber(formatInputMDNDisplay(value)))
                        ? null
                        : "Invalid phone number";
                }
            },
        },
    });
    const handleContactChange = (inputValue: string) => {
        if (isEmailType()) {
            form.setFieldValue("contact", inputValue);
        } else {
            const formattedPhone = formatInputMDNDisplay(inputValue);
            form.setFieldValue("contact", formattedPhone);
        }
    };

    const handleSendLinkClick = async () => {
        if (form.validate().hasErrors) {
            logger.error("Send link validation failed");
            return;
        }

        try {
            setLoading(true);
            if (isEmailType()) {
                updateEmail(form.values.contact);
            } else {
                updateMDN(form.values.contact);
            }

            const contactToSend = isEmailType() ? form.values.contact : formatInputMDN(form.values.contact);
            const isMDNUpdated = !isEmailType() && customer?.mdn !== contactToSend;
            if (isMDNUpdated) {
                void dispatcher.withExtra({ step: integratedStep }).dispatchBusinessEvent("MDN_Updated");
            }
            const response = await sendLink(commType, contactToSend);

            useProductOfferStore
                .getState()
                .updateSmartLinkMetaBySessionId(sessionId, selectedProduct?.id ?? primaryProducts[0].id, {
                    maximumSmartLinksGenerated: response.maxAttemptsReached,
                });

            void dispatcher.withExtra({ nextStep: integratedStep }).dispatchBusinessEvent("ButtonClicked_SendLink");
            updateIntegratedStep(
                sessionId,
                product.confirmationCodePattern ? "InlineSaleTrackerStep" : "WaitingForEnrollmentStep",
            );
        } catch (error) {
            if (error instanceof HTTPError && error.response.status === 403) {
                toast.error("You have reached the maximum number of attempts to send a link.");
            } else {
                toast.error("Failed to send link. Please try again.");
            }
            logger.error({ error }, "Failed to send link:");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Flex direction={"column"} justify={"space-between"}>
            {isChat ? (
                <Flex direction="column" px="sm" gap={12} justify={"center"} align="center">
                    <Box py="md">
                        <SendDisclosuresIcon />
                    </Box>
                    <Flex direction="column" gap={12} align="center">
                        <Text fz="h5" fw="700" c="dark.1">
                            Send the disclosures
                        </Text>
                        <Text size="xs" c="dark.2" ta="center">
                            Once you send the link, the customer will need to review and accept the disclosures.
                        </Text>
                    </Flex>
                </Flex>
            ) : (
                <Flex mt={24} mb={32} px={24} gap={24} direction="column" w="100%">
                    <Text fz="h5" fw="700">
                        Send a link for enrollment
                    </Text>
                    <Flex direction="column" gap="xs">
                        <Text size="xs">Please ask:</Text>
                        <Flex gap="xs">
                            <Flex>
                                <GreenQuote />
                            </Flex>
                            <Text size="xs">{getSendLinkCopy(sendLinkCommTypeOverride)}</Text>
                        </Flex>
                    </Flex>
                    {!sendLinkCommTypeOverride && (
                        <CommunicationTypeSelector
                            commType={commType}
                            setCommType={setCommType}
                            form={form}
                            email={email ?? ""}
                            mdn={mdn}
                        />
                    )}
                    <form
                        onSubmit={(event) => {
                            event.preventDefault();
                        }}
                    >
                        <TextInput
                            className="fs-mask"
                            classNames={{ input: classes.textAreaStyle }}
                            key={commType}
                            label={commType === "EMAIL" ? "Email address" : "Phone number"}
                            {...form.getInputProps("contact")}
                            size="xs"
                            onChange={(event) => handleContactChange(event.currentTarget.value)}
                        />
                        {helpButtonEnabled && (
                            <Button
                                onClick={() => updateIntegratedStep(sessionId, "HelpCustomerFindSubscribeButtonStep")}
                                classNames={{ root: classes.linkHelpButton }}
                                size="xxs"
                                p={0}
                                variant="transparent"
                            >
                                <Text size="xs" td="underline">
                                    Continue without sending link
                                </Text>
                            </Button>
                        )}
                    </form>
                </Flex>
            )}
            <SendLinkFooter
                handleSendLinkClick={() => {
                    void handleSendLinkClick();
                }}
                isCommunicationInfoCollected={form.isValid()}
                isLoading={loading}
                integratedStep={integratedStep}
                isChat={isChat}
            />
        </Flex>
    );
}
